import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import successCheckIcon from "@assets/facilityNetwork/share-success.svg";
import chevronWhiteIcon from "@assets/facilityNetwork/ni-chevron-white.svg";
import chevronTealIcon from "@assets/facilityNetwork/ni-chevron-teal.svg";
import {
  fetchIntelligenceDraft,
  fetchSubmittedPortfolio,
  portfolioHasVisibleFacilities,
} from "@services/networkIntelligence";
import { listNetworks } from "@services/networkManagement";
import { userCanUseNetworkFacility } from "@utils/userUtils";
import FacilityNetworkFigmaIcon from "../facilityNetwork/FacilityNetworkFigmaIcon";
import FacilityNetworkPageShell from "../facilityNetwork/FacilityNetworkPageShell";
import { FN } from "../facilityNetwork/facilityNetworkTheme";
import NetworkIntelligenceLoader from "./NetworkIntelligenceLoader";
import NetworkIntelligenceManage from "./NetworkIntelligenceManage";
import NetworkIntelligenceReview from "./NetworkIntelligenceReview";
import NetworkIntelligenceUpload from "./NetworkIntelligenceUpload";

export const NETWORK_INTELLIGENCE_TALK_MAILTO =
  "mailto:contact@unicomply.ai?subject=Network%20Intelligence%20walkthrough";

const GRADIENT = "linear-gradient(90deg, #0a829d 0%, #58c2d8 100%)";
const CARD_SHADOW = FN.figmaCardShadow;

const DIFFERENTIATORS = [
  {
    title: "Any portfolio, defined by you",
    body: "Monitor facilities you own, facilities you're evaluating for acquisition, or facilities you track for market intelligence. No consent required from the facilities themselves - unlike your consented network view, this scope is entirely yours to define.",
  },
  {
    title: "Quarter-by-quarter depth, drill to any score",
    body: "Three years of exposure history sliced quarter by quarter. Click into any quarter to see what drove the numbers, or into any facility to see individual scores, deficiency history, staffing patterns, and quality trends.",
  },
  {
    title: "Know your coverage gaps",
    body: "See exactly how much of your portfolio's regulatory activity Unicomply actively monitors for you — and where your compliance blind spots are. Move facilities from unmonitored to actively tracked as your priorities shift.",
  },
] as const;

const BUILT_FOR = [
  {
    title: "Multi-facility health systems",
    body: "Operators managing 10+ facilities across regions, types, or ownership structures.",
  },
  {
    title: "PE portfolios & M&A teams",
    body: "Evaluate compliance exposure across potential acquisitions or existing portfolio holdings.",
  },
  {
    title: "Enterprise compliance ops",
    body: "Compliance leaders overseeing risk across a broad and mixed facility footprint.",
  },
] as const;

const primaryCtaSx = {
  textTransform: "none" as const,
  height: 32,
  minHeight: 32,
  px: 2,
  py: 1,
  borderRadius: "4px",
  fontFamily: FN.fontFamily,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "20px",
  color: "#fff",
  background: GRADIENT,
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)",
  "& .MuiButton-endIcon": { ml: "4px", mr: 0 },
  "&:hover": {
    background: "linear-gradient(90deg, #065b70 0%, #0a829d 100%)",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)",
  },
};

const onGradientCtaSx = {
  ...primaryCtaSx,
  color: FN.primary,
  background: "#fff",
  "&:hover": {
    background: "#f3fdff",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)",
  },
};

function intelligenceStep(pathname: string): "landing" | "upload" | "review" | "manage" {
  const path = pathname.replace(/\/+$/, "");
  if (path.endsWith("/upload")) return "upload";
  if (path.endsWith("/review")) return "review";
  if (path.endsWith("/manage")) return "manage";
  return "landing";
}

function ManagePortfolioButton() {
  const navigate = useNavigate();
  return (
    <Button
      onClick={() =>
        navigate("/dashboard/network/intel/manage", {
          state: { currentView: "network-intelligence" },
        })
      }
      sx={{
        textTransform: "none",
        fontFamily: FN.fontFamily,
        fontWeight: 600,
        color: FN.primary,
        px: 0,
        minWidth: 0,
      }}
    >
      Manage portfolio
    </Button>
  );
}

function GetStartedButton() {
  const navigate = useNavigate();
  return (
    <Button
      onClick={() =>
        navigate("/dashboard/network/intel/upload", {
          state: { currentView: "network-intelligence" },
        })
      }
      endIcon={<FacilityNetworkFigmaIcon src={chevronWhiteIcon} size={12} />}
      sx={primaryCtaSx}
    >
      Get started
    </Button>
  );
}

function TalkToTeamButton({ variant }: { variant: "primary" | "onGradient" }) {
  const onGradient = variant === "onGradient";
  return (
    <Button
      component="a"
      href={NETWORK_INTELLIGENCE_TALK_MAILTO}
      endIcon={
        <FacilityNetworkFigmaIcon
          src={onGradient ? chevronTealIcon : chevronWhiteIcon}
          size={12}
        />
      }
      sx={onGradient ? onGradientCtaSx : primaryCtaSx}
    >
      Talk to our team
    </Button>
  );
}

export default function NetworkIntelligencePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const step = intelligenceStep(location.pathname);
  const [dashboardNetworkId, setDashboardNetworkId] = useState<number | null>(null);
  const [submittedGate, setSubmittedGate] = useState<"checking" | "open" | "sales">("checking");
  const canUseNetworkFacility = userCanUseNetworkFacility();

  useEffect(() => {
    if (step !== "landing") return undefined;
    let cancelled = false;
    Promise.all([
      fetchIntelligenceDraft().catch(() => null),
      fetchSubmittedPortfolio().catch(() => null),
    ])
      .then(([draft, submitted]) => {
        if (cancelled) return;
        const open =
          portfolioHasVisibleFacilities(draft) ||
          portfolioHasVisibleFacilities(submitted);
        setSubmittedGate(open ? "open" : "sales");
      })
      .catch(() => {
        if (!cancelled) setSubmittedGate("sales");
      });
    return () => {
      cancelled = true;
    };
  }, [step]);

  useEffect(() => {
    if (step !== "landing" || !canUseNetworkFacility) {
      setDashboardNetworkId(null);
      return;
    }
    let cancelled = false;
    listNetworks()
      .then((rows) => {
        if (!cancelled) setDashboardNetworkId(rows[0]?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setDashboardNetworkId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [canUseNetworkFacility, step]);

  const openDashboard = () => {
    if (dashboardNetworkId == null) return;
    navigate("/dashboard/facilities", {
      state: { currentView: "network", networkId: dashboardNetworkId },
    });
  };

  if (step === "upload") return <NetworkIntelligenceUpload />;
  if (step === "review") return <NetworkIntelligenceReview />;
  if (step === "manage") return <NetworkIntelligenceManage />;
  if (submittedGate === "checking") {
    return (
      <FacilityNetworkPageShell>
        <NetworkIntelligenceLoader />
      </FacilityNetworkPageShell>
    );
  }
  if (submittedGate === "open") return <NetworkIntelligenceReview />;

  return (
    <FacilityNetworkPageShell>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: FN.figmaTitleSize,
              fontWeight: 600,
              color: FN.title,
              letterSpacing: FN.figmaTitleTracking,
              lineHeight: "32px",
            }}
          >
            Network Intelligence
          </Typography>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 16,
              fontWeight: 600,
              color: FN.figmaNeutral,
              lineHeight: "22px",
            }}
          >
            See compliance risk across any portfolio — including facilities you don't own.
          </Typography>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 12,
              fontWeight: 400,
              color: FN.figmaAccent,
              letterSpacing: "0.03px",
              lineHeight: "16px",
              overflowWrap: "anywhere",
            }}
          >
            Network Intelligence gives multi-facility operators a portfolio-level view of
            exposure, quarter-by-quarter historical depth, and facility-level operational
            signals — for any set of facilities you define. Consent isn't required from the
            facilities themselves.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start" }}>
          <GetStartedButton />
          <ManagePortfolioButton />
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 12,
              color: FN.figmaAccent,
              letterSpacing: "0.03px",
              lineHeight: "16px",
            }}
          >
            We'll walk you through what it does and how to define your portfolio.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", minWidth: 0, maxWidth: "100%" }}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 16,
              fontWeight: 600,
              color: FN.primary,
              lineHeight: "22px",
            }}
          >
            What makes it different
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
              alignItems: "stretch",
              bgcolor: "#fff",
              border: `1px solid ${FN.figmaCardBorder}`,
              borderRadius: FN.figmaCardRadius,
              boxShadow: CARD_SHADOW,
              pt: 1,
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              boxSizing: "border-box",
            }}
          >
            {DIFFERENTIATORS.map((item) => (
              <Box
                key={item.title}
                sx={{
                  minWidth: 0,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <FacilityNetworkFigmaIcon src={successCheckIcon} size={16} />
                </Box>
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 14,
                    fontWeight: 600,
                    color: FN.figmaNeutral,
                    lineHeight: "20px",
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 14,
                    fontWeight: 400,
                    color: FN.figmaNeutral,
                    lineHeight: "20px",
                    overflowWrap: "anywhere",
                  }}
                >
                  {item.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: "#fff",
            border: `1px solid ${FN.figmaCardBorder}`,
            borderRadius: FN.figmaCardRadius,
            boxShadow: CARD_SHADOW,
            px: 1,
            py: 2,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 16,
              fontWeight: 600,
              color: FN.primary,
              lineHeight: "22px",
              px: 1,
              mb: 0,
            }}
          >
            Built for
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
              gap: 1,
              width: "100%",
              minWidth: 0,
            }}
          >
            {BUILT_FOR.map((item) => (
              <Box key={item.title} sx={{ minWidth: 0, px: 1, py: 2 }}>
                <Box
                  sx={{
                    borderLeft: `2px solid ${FN.primary}`,
                    pl: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: FN.fontFamily,
                      fontSize: 14,
                      fontWeight: 600,
                      color: FN.figmaNeutral,
                      lineHeight: "20px",
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: FN.fontFamily,
                      fontSize: 12,
                      fontWeight: 400,
                      color: FN.figmaNeutral,
                      letterSpacing: "0.03px",
                      lineHeight: "16px",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {item.body}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: 1,
            p: 4,
            minHeight: { md: 130 },
            boxSizing: "border-box",
            borderRadius: FN.figmaCardRadius,
            background: GRADIENT,
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: FN.figmaTitleSize,
                fontWeight: 600,
                color: "#fff",
                letterSpacing: FN.figmaTitleTracking,
                lineHeight: "32px",
              }}
            >
              Ready to see it in action?
            </Typography>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 18,
                fontWeight: 400,
                color: "#fff",
                letterSpacing: "-0.18px",
                lineHeight: 1.5,
                overflowWrap: "anywhere",
              }}
            >
              Reach out to our team for a walkthrough tailored to your portfolio and how you'd
              like to define your network.
            </Typography>
          </Box>
          <Box sx={{ flexShrink: 0, p: 2 }}>
            <TalkToTeamButton variant="onGradient" />
          </Box>
        </Box>

        {canUseNetworkFacility && dashboardNetworkId != null ? (
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <Button
              onClick={openDashboard}
              endIcon={<FacilityNetworkFigmaIcon src={chevronTealIcon} size={12} />}
              sx={{
                textTransform: "none",
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: "20px",
                color: FN.primary,
                px: 1.5,
                py: 0.75,
                minHeight: 32,
                "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
              }}
            >
              View your dashboard
            </Button>
          </Box>
        ) : null}
      </Box>
    </FacilityNetworkPageShell>
  );
}
